import InlineMessage from '@ingka/inline-message';
import Loading, { LoadingBall } from '@ingka/loading';
import Tabs, { Tab, TabPanel } from '@ingka/tabs';
import Text from '@ingka/text';
import Button from '@ingka/button';
import { useGetRetailUnitQuery } from '@lotta/self-service/lottery-fe/data-access-state';
import { useLocale } from '@lotta/self-service/lottery-fe/feature-hooks';
import type { Static } from '@sinclair/typebox';
import { Type } from '@sinclair/typebox';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Control } from 'react-hook-form';
import { transformBody } from '@lotta/self-service/lottery-fe/util-utilities';
import { swipeImageDomainWhitelistPattern } from '@lotta/shared/types-lottery-schemas';
import styles from '../../LotteryForm.module.scss';
import { ControlledInputField } from '../../components/controlled-input-field/ControlledInputField';
import { ControlledTextArea } from '../../components/controlled-text-area/ControlledTextArea';
import type { SendPreviewEmailButtonControlType } from '../../components/send-preview-email-button/SendPreviewEmailButton';
import { SendPreviewEmailButton } from '../../components/send-preview-email-button/SendPreviewEmailButton';
import { mapRecord } from '../../utils';
import { useFormModule } from '../../utils/use-form-module';
import { FindImageInfoTheatre } from '../../components/find-image-info-theatre/FindImageInfoTheatre';
import type { FormModuleProps, ModuleTransformer } from './../types';
export const MODULE_NAME = 'email';

export type EmailFormType = Static<typeof EmailFormSchema>;
export const EmailFormSchema = Type.Object(
  {
    translation: Type.Record(
      Type.String(), // language code
      Type.Object({
        subjectLine: {
          ...Type.String({ minLength: 1, maxLength: 100 }),
          errorMessage: {
            minLength: 'form-lottery-name-error-message-min-length',
            maxLength: 'form-lottery-name-error-message-max-length',
          },
        },
        preHeader: {
          ...Type.String({ minLength: 1, maxLength: 50 }),
          errorMessage: {
            minLength: 'form-lottery-name-error-message-min-length',
            maxLength: 'form-lottery-name-error-message-max-length',
          },
        },
        bodyHeadline: {
          ...Type.String({ minLength: 1, maxLength: 100 }),
          errorMessage: {
            minLength: 'form-lottery-name-error-message-min-length',
            maxLength: 'form-lottery-name-error-message-max-length',
          },
        },
        ctaText: {
          ...Type.String({ minLength: 1, maxLength: 50 }),
          errorMessage: {
            minLength: 'form-lottery-name-error-message-min-length',
            maxLength: 'form-lottery-name-error-message-max-length',
          },
        },
        ctaLink: {
          ...Type.String({ format: 'uri', maxLength: 1000 }),
          errorMessage: {
            minLength: 'form-lottery-name-error-message-min-length',
            maxLength: 'form-lottery-name-error-message-max-length',
          },
        },
        disclaimerText: {
          ...Type.Optional(Type.String({ minLength: 0, maxLength: 1000 })),
          errorMessage: {
            minLength: 'form-lottery-name-error-message-min-length',
            maxLength: 'form-lottery-name-error-message-max-length',
          },
        },
        body: {
          ...Type.String({ minLength: 1, maxLength: 1500 }),
          errorMessage: {
            minLength: 'form-lottery-name-error-message-min-length',
            maxLength: 'form-lottery-name-error-message-max-length',
          },
        },
      })
    ),
    bannerImageUrl: Type.String({
      format: 'uri',
      maxLength: 1000,
      pattern: swipeImageDomainWhitelistPattern,
      errorMessage: {
        format: 'form-error-message-uri',
        pattern: 'form-error-message-image-pattern',
        maxLength: 'form-lottery-name-error-message-max-length',
      },
    }),
  },
  { additionalProperties: false, $id: 'EmailFormSchema' }
);

export const EmailFormTransformer: ModuleTransformer<EmailFormType> = {
  fromPayload: ({ translation, lottery }) => {
    return {
      translation: mapRecord(translation, (translation) => ({
        subjectLine: translation.email?.winner.subjectLine ?? '',
        preHeader: translation.email?.winner.preHeader ?? '',
        bodyHeadline: translation.email?.winner.bodyHeadline ?? '',
        ctaText: translation.email?.winner.ctaText ?? '',
        ctaLink: translation.email?.winner.ctaLink ?? '',
        disclaimerText: translation.email?.winner.disclaimerText ?? '',
        body:
          translation.email?.winner.body.copy.map(({ bodyCopy }) => bodyCopy).join('\n\n') ?? '',
      })),
      bannerImageUrl: lottery.emailTemplate?.bannerImageUrl ?? '',
    };
  },
  toPayload: (formData) => {
    return {
      translation: mapRecord(formData.translation, (translation) => ({
        email: {
          winner: {
            subjectLine: translation.subjectLine,
            preHeader: translation.preHeader,
            bodyHeadline: translation.bodyHeadline,
            ctaText: translation.ctaText,
            ctaLink: translation.ctaLink,
            disclaimerText: translation.disclaimerText ?? '',
            body: transformBody(translation.body),
          },
        },
      })),
      lottery: {
        emailTemplate: {
          bannerImageUrl: formData.bannerImageUrl,
        },
      },
    };
  },
};

const defaultValues: EmailFormType = { translation: {}, bannerImageUrl: '' };

export type EmailFormProps = FormModuleProps<EmailFormType>;

export const EmailForm: React.FC<EmailFormProps> = memo(
  ({ retailUnit, values, onChange, onValidation }) => {
    const { t } = useTranslation();
    const {
      data: retailUnitData,
      isLoading: retailUnitIsLoading,
      error: retailUnitError,
    } = useGetRetailUnitQuery({ retailUnit });

    // Set default value for bannerImageUrl from retailUnitData
    defaultValues.bannerImageUrl = retailUnitData?.retailUnit.emailTemplate.bannerImageUrl ?? '';

    const {
      control,
      formState: { isValid },
    } = useFormModule({
      moduleName: MODULE_NAME,
      defaultValues: values || defaultValues,
      schema: EmailFormSchema,
      onChange,
      onValidation,
    });
    const { language } = useLocale();
    const [open, setOpen] = useState(false);

    if (retailUnitIsLoading) {
      return (
        <Loading text={`${t('general-fetching')}`}>
          <LoadingBall />
        </Loading>
      );
    }

    if (retailUnitError || !retailUnitData) {
      return <Text headingSize="m">{t('general-something-went-wrong')}</Text>;
    }

    const languageNames = new Intl.DisplayNames([language], { type: 'language' });

    const tabsAndPanels = retailUnitData.retailUnit.languages.map((language) => {
      const id = `claim-prize-tab--${language}`;
      return {
        id,
        tab: <Tab key={language} text={languageNames.of(language)} tabPanelId={id} type="button" />,
        panel: (
          <TabPanel key={language} tabPanelId={id}>
            <div className={styles['form-elements']}>
              <ControlledInputField
                control={control}
                labelKey="lottery-form-email-form-email-subject-line"
                name={`translation.${language}.subjectLine`}
                showCharacterLimit={true}
                upperLimit={
                  EmailFormSchema.properties.translation.patternProperties['^.*$']?.properties
                    ?.subjectLine.maxLength
                }
              />
              <ControlledInputField
                control={control}
                labelKey="lottery-form-email-form-email-pre-header"
                name={`translation.${language}.preHeader`}
                showCharacterLimit={true}
                upperLimit={
                  EmailFormSchema.properties.translation.patternProperties['^.*$']?.properties
                    ?.preHeader?.maxLength
                }
              />
              <ControlledInputField
                control={control}
                labelKey="lottery-form-email-form-email-body-title"
                name={`translation.${language}.bodyHeadline`}
                showCharacterLimit={true}
                upperLimit={
                  EmailFormSchema.properties.translation.patternProperties['^.*$']?.properties
                    ?.bodyHeadline?.maxLength
                }
              />
              <ControlledTextArea
                control={control}
                labelKey="lottery-form-email-form-email-body-content"
                name={`translation.${language}.body`}
                showCharacterLimit={false}
                upperLimit={
                  EmailFormSchema.properties.translation.patternProperties['^.*$']?.properties?.body
                    ?.maxLength
                }
              />
              <ControlledInputField
                control={control}
                labelKey="lottery-form-email-form-email-disclaimer"
                name={`translation.${language}.disclaimerText`}
                showCharacterLimit={true}
                upperLimit={
                  EmailFormSchema.properties.translation.patternProperties['^.*$']?.properties
                    ?.disclaimerText?.maxLength
                }
              />
              <ControlledInputField
                control={control}
                labelKey="lottery-form-email-form-email-button"
                name={`translation.${language}.ctaText`}
                showCharacterLimit={true}
                upperLimit={
                  EmailFormSchema.properties.translation.patternProperties['^.*$']?.properties
                    ?.ctaText?.maxLength
                }
              />
              <ControlledInputField
                control={control}
                labelKey="lottery-form-email-form-email-button-link"
                name={`translation.${language}.ctaLink`}
                showCharacterLimit={true}
                upperLimit={
                  EmailFormSchema.properties.translation.patternProperties['^.*$']?.properties
                    ?.ctaLink?.maxLength
                }
              />
            </div>
            <SendPreviewEmailButton
              retailUnit={retailUnit}
              language={language}
              control={control as unknown as Control<SendPreviewEmailButtonControlType>}
            />
            <InlineMessage
              className={styles['inline-message']}
              variant="cautionary"
              title={`${t('form-warning-message-spam-email-title')}`}
              body={`${t('form-warning-message-spam-email-body')}`}
            />
          </TabPanel>
        ),
      };
    });

    const tabs = tabsAndPanels.map(({ tab }) => tab);
    const panels = tabsAndPanels.map(({ panel }) => panel);

    return (
      <>
        <InlineMessage
          className={styles['inline-message']}
          variant="informative"
          title={`${t('form-explanation-message-winner-email-title')}`}
          body={`${t('form-explanation-message-winner-email-body')}`}
        />
        {!isValid && (
          <InlineMessage
            className={styles['inline-message']}
            variant="cautionary"
            title={`${t('form-missing-translations-title')}`}
            body={`${t('form-missing-translations-body')}`}
          />
        )}
        <form>
          <ControlledTextArea
            control={control}
            labelKey="lottery-form-email-form-email-image-label"
            name="bannerImageUrl"
            showCharacterLimit
            upperLimit={EmailFormSchema.properties.bannerImageUrl.maxLength}
            helpText={'https://www.ikea.com/ext/ingkadam/...'}
          />

          <FindImageInfoTheatre open={open} onClose={() => setOpen(false)} />
          <Button
            text={t('find-image-theatre-button-text')}
            className={styles['find-image-button']}
            type="secondary"
            onClick={() => setOpen(true)}
          />
          <Tabs tabs={tabs} tabPanels={panels} defaultActiveTab={tabsAndPanels[0].id} />
        </form>
      </>
    );
  }
);

export const module = {
  name: MODULE_NAME,
  transformer: EmailFormTransformer,
  form: EmailForm,
} as const;
